import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx'; // Import XLSX library

function NewCall({ selectedCompany }) { // selectedCompany will be 'postsurgery_followups'
  // Form state for a single patient entry
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [mrn, setMrn] = useState('');
  const [surgeryDate, setSurgeryDate] = useState('');
  const [surgeryType, setSurgeryType] = useState('');
  const [surgerySite, setSurgerySite] = useState('');
  // Scenario is removed from form state, will be hardcoded or implicitly 'post_surgery'

  // Queue state
  const [patientQueue, setPatientQueue] = useState([]);
  const [isCallingQueue, setIsCallingQueue] = useState(false);
  const [callStatuses, setCallStatuses] = useState({}); // Stores status for each patient in queue by unique ID

  // General loading/status for single operations if needed (though primary focus is queue)
  const [isLoading, setIsLoading] = useState(false); 
  const [feedbackMessage, setFeedbackMessage] = useState(null); // { type: 'success' | 'error', message: '...'}


  const scenarios = [
    { value: 'default', label: 'Default (standard follow-up call)' },
    { value: 'post_surgery', label: 'Post-surgery (specific follow-up)' },
    { value: 'appointment_reminder', label: 'Appointment reminder' },
  ];

  const handleAddPatientToQueue = () => {
    if (!name.trim() || !phone.trim()) {
      setFeedbackMessage({ type: 'error', message: 'Name and Phone Number are required to add a patient.' });
      return;
    }
    // Basic date validation (within 30 days from today for surgery date)
    if (surgeryDate) {
        const today = new Date();
        const sDate = new Date(surgeryDate);
        const thirtyDaysAgo = new Date(today.setDate(today.getDate() - 30));
        today.setDate(today.getDate() + 30); // Reset today to actual today for comparison if needed elsewhere

        if (sDate < thirtyDaysAgo || sDate > new Date()) {
             setFeedbackMessage({ type: 'error', message: 'Surgery Date must be within the last 30 days and not in the future.' });
             return;
        }
    }


    const newPatient = {
      id: Date.now().toString(), // Simple unique ID
      name,
      phone,
      dob,
      mrn,
      surgeryDate,
      surgeryType,
      surgerySite,
      scenario: 'post_surgery',
      status: 'Pending' // Initial status
    };
    setPatientQueue(prevQueue => [...prevQueue, newPatient]);
    setFeedbackMessage({ type: 'success', message: `${name} added to the queue.` });

    // Clear form fields
    setName('');
    setPhone('');
    setDob('');
    setMrn('');
    setSurgeryDate('');
    setSurgeryType('');
    setSurgerySite('');
    // setScenario('default'); // Optionally reset scenario or keep it
  };

  const handleRemovePatientFromQueue = (patientId) => {
    setPatientQueue(prevQueue => prevQueue.filter(p => p.id !== patientId));
    setCallStatuses(prevStatuses => {
      const newStatuses = {...prevStatuses};
      delete newStatuses[patientId];
      return newStatuses;
    });
  };

  const handleInitiateSingleCall = async (patient) => {
    setCallStatuses(prev => ({ ...prev, [patient.id]: { status: 'Calling...', message: '' } }));

    const formData = new URLSearchParams();
    formData.append('phone_number', patient.phone);
    formData.append('name', patient.name);
    formData.append('scenario', 'post_surgery'); // Hardcode scenario
    // Add new fields
    if (patient.dob) formData.append('dob', patient.dob); // Assuming backend accepts 'dob'
    if (patient.mrn) formData.append('mrn', patient.mrn); // Assuming backend accepts 'mrn'
    if (patient.surgeryDate) formData.append('surgery_date', patient.surgeryDate);
    if (patient.surgeryType) formData.append('surgery_type', patient.surgeryType);
    if (patient.surgerySite) formData.append('surgery_site', patient.surgerySite);
    
    // Add other optional fields the backend /start-call endpoint might accept
    // e.g., follow_up_type, child_name, surgery_date_approx
    // For now, these are not explicitly in the form, but could be added or derived.

    try {
      const backendUrl = process.env.REACT_APP_OUTBOUND_API_URL || 'http://localhost:8000';
      const endpoint = `${backendUrl}/start-call`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      let result;
      const responseText = await response.text();
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        throw new Error(`Server returned non-JSON: ${response.status}. Body: ${responseText.substring(0,100)}`);
      }

      if (!response.ok) {
        throw new Error(result.detail || result.message || `HTTP error! Status: ${response.status}`);
      }
      
      setCallStatuses(prev => ({ ...prev, [patient.id]: { status: 'Call Initiated', success: true, message: `Call SID: ${result.call_sid || 'N/A'}` } }));
      return { success: true, call_sid: result.call_sid };

    } catch (error) {
      console.error(`Error initiating call for ${patient.name}:`, error);
      setCallStatuses(prev => ({ ...prev, [patient.id]: { status: 'Failed', success: false, message: error.message } }));
      return { success: false, message: error.message };
    }
  };

  const handleStartCallingQueue = async () => {
    if (patientQueue.length === 0) {
      setFeedbackMessage({ type: 'error', message: 'Patient queue is empty.' });
      return;
    }
    setIsCallingQueue(true);
    setFeedbackMessage({type: 'info', message: 'Starting to call patients in the queue...'});

    for (const patient of patientQueue) {
      // Check if already processed or being processed to avoid re-calling if button is spammed
      // This simple check might need enhancement for robustness (e.g. if a call failed and user wants to retry *that specific one*)
      if (callStatuses[patient.id]?.status === 'Call Initiated' || callStatuses[patient.id]?.status === 'Calling...') {
          console.log(`Skipping ${patient.name}, call already initiated or in progress.`);
          continue;
      }
      await handleInitiateSingleCall(patient);
      // Optional: Add a small delay between calls if needed
      // await new Promise(resolve => setTimeout(resolve, 1000)); 
    }
    setIsCallingQueue(false);
    setFeedbackMessage({type: 'info', message: 'Finished processing the patient queue.'});
  };
  
  // Only render if selectedCompany is 'postsurgery_followups'
  if (selectedCompany !== 'postsurgery_followups') {
    return null; 
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow space-y-8">
      {/* Patient Entry Form */}
      <section>
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Add Patient to Calling Queue (Post-Surgery Follow-ups)</h3>
          <p className="text-sm text-gray-600 mt-1">
            Enter patient details. They will be added to a list and called sequentially.
          </p>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
            <input type="text" name="name" id="name" value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Full name"/>
          </div>
          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Phone Number <span className="text-red-500">*</span></label>
            <input type="tel" name="phone" id="phone" value={phone} onChange={(e) => setPhone(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="+12223334444"/>
          </div>
          {/* DOB */}
          <div>
            <label htmlFor="dob" className="block text-sm font-medium text-gray-700">Date of Birth</label>
            <input type="date" name="dob" id="dob" value={dob} onChange={(e) => setDob(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"/>
          </div>
          {/* MRN */}
          <div>
            <label htmlFor="mrn" className="block text-sm font-medium text-gray-700">MRN</label>
            <input type="text" name="mrn" id="mrn" value={mrn} onChange={(e) => setMrn(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Medical Record Number"/>
          </div>
          {/* Surgery Date */}
          <div>
            <label htmlFor="surgeryDate" className="block text-sm font-medium text-gray-700">Date of Surgery</label>
            <input type="date" name="surgeryDate" id="surgeryDate" value={surgeryDate} onChange={(e) => setSurgeryDate(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"/>
          </div>
          {/* Surgery Type */}
          <div>
            <label htmlFor="surgeryType" className="block text-sm font-medium text-gray-700">Surgery Type</label>
            <input type="text" name="surgeryType" id="surgeryType" value={surgeryType} onChange={(e) => setSurgeryType(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Appendectomy"/>
          </div>
          {/* Surgery Site */}
          <div className="md:col-span-2">
            <label htmlFor="surgerySite" className="block text-sm font-medium text-gray-700">Surgery Site</label>
            <input type="text" name="surgerySite" id="surgerySite" value={surgerySite} onChange={(e) => setSurgerySite(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Left Knee"/>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <button type="button" onClick={handleAddPatientToQueue} disabled={isLoading || isCallingQueue}
            className="w-full sm:w-auto flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50">
            Add Patient to Queue
          </button>
           <input 
              type="file" 
              id="excelUpload" 
              className="hidden" 
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              onChange={async (e) => {
                if (e.target.files && e.target.files[0]) {
                  const file = e.target.files[0];
                  setFeedbackMessage({type: 'info', message: `Processing ${file.name}...`});
                  setIsLoading(true); // Use general isLoading or a specific one for file processing

                  try {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      try {
                        const data = event.target.result;
                        if (!XLSX) {
                            throw new Error("XLSX library is not available. Please ensure it\'s installed and imported.");
                        }
                        // Add cellDates: true to encourage parsing of dates by xlsx library
                        const workbook = XLSX.read(data, { type: 'binary', cellDates: true });
                        const sheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[sheetName];
                        // Use raw: false to get formatted strings if cellDates didn't convert, but prefer converted dates
                        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false });

                        if (jsonData.length < 2) { // At least one header row and one data row
                          throw new Error("Excel file is empty or has no data rows.");
                        }

                        const headers = jsonData[0].map(header => String(header).trim().toLowerCase());
                        const patientsFromFile = [];

                        const headerMapping = {
                          'name': 'name',
                          'dob': 'dob',
                          'date of surgery': 'surgeryDate',
                          'surgery': 'surgeryType',
                          'phone number': 'phone',
                          'mrn': 'mrn',
                          'surgery site': 'surgerySite'
                        };

                        for (let i = 1; i < jsonData.length; i++) {
                          const row = jsonData[i];
                          if (row.filter(String).length === 0) continue;

                          const patientData = {};
                          headers.forEach((header, index) => {
                            const patientKey = Object.keys(headerMapping).find(key => header.includes(key));
                            if (patientKey) {
                                let value = row[index];
                                if (headerMapping[patientKey] === 'dob' || headerMapping[patientKey] === 'surgeryDate') {
                                    let formattedDate = null;
                                    if (value instanceof Date) {
                                        // Value is a JavaScript Date object (hopefully from cellDates:true)
                                        // Format to YYYY-MM-DD. toISOString provides UTC.
                                        formattedDate = value.toISOString().split('T')[0];
                                    } else if (typeof value === 'number') {
                                        // Fallback for Excel serial number if cellDates:true didn't convert
                                        const dateObj = new Date(Math.round((value - 25569) * 86400 * 1000));
                                        if (!isNaN(dateObj.getTime())) {
                                            formattedDate = dateObj.toISOString().split('T')[0];
                                        }
                                    } else if (typeof value === 'string' && value.trim() !== '') {
                                        const s_val = value.trim();
                                        let y, m, d;
                                        // Try YYYY-MM-DD or YYYY/MM/DD
                                        const partsYMD = s_val.match(/^(\\d{4})[\\.\\/\\-](\\d{1,2})[\\.\\/\\-](\\d{1,2})$/);
                                        // Try DD.MM.YYYY or DD/MM/YYYY (covers MM.DD.YYYY too, disambiguate later)
                                        const partsDMY = s_val.match(/^(\\d{1,2})[\\.\\/\\-](\\d{1,2})[\\.\\/\\-](\\d{4})$/);

                                        if (partsYMD) {
                                            y = parseInt(partsYMD[1], 10);
                                            m = parseInt(partsYMD[2], 10);
                                            d = parseInt(partsYMD[3], 10);
                                        } else if (partsDMY) {
                                            const p1 = parseInt(partsDMY[1], 10);
                                            const p2 = parseInt(partsDMY[2], 10);
                                            const p3 = parseInt(partsDMY[3], 10);
                                            // Basic disambiguation for D/M vs M/D
                                            if (p1 > 12 && p2 <= 12) { // Likely DD.MM.YYYY
                                                d = p1; m = p2; y = p3;
                                            } else if (p2 > 12 && p1 <= 12) { // Likely MM.DD.YYYY
                                                d = p2; m = p1; y = p3;
                                            } else { // Ambiguous or both <=12, assume DD.MM.YYYY as per your primary locale
                                                d = p1; m = p2; y = p3;
                                            }
                                        }

                                        if (y && m && d) {
                                            // Validate month and day ranges
                                            if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
                                                const dateObj = new Date(Date.UTC(y, m - 1, d));
                                                // Double check if the date constructor didn't roll over (e.g. Feb 30 -> Mar 2)
                                                if (!isNaN(dateObj.getTime()) && dateObj.getUTCFullYear() === y && dateObj.getUTCMonth() === m - 1 && dateObj.getUTCDate() === d) {
                                                    formattedDate = dateObj.toISOString().split('T')[0];
                                                }
                                            }
                                        }
                                        
                                        // If still no formattedDate, and it's a string that looks like an Excel serial number
                                        if (!formattedDate) {
                                            const numVal = parseFloat(s_val);
                                            if (!isNaN(numVal) && String(numVal) === s_val && numVal > 0 && numVal < 200000) { // Heuristic
                                                const dateObj = new Date(Math.round((numVal - 25569) * 86400 * 1000));
                                                if (!isNaN(dateObj.getTime())) {
                                                    formattedDate = dateObj.toISOString().split('T')[0];
                                                }
                                            }
                                        }
                                    }
                                    // Assign the formatted date, or original value if formatting failed (though input type=date might not like it)
                                    patientData[headerMapping[patientKey]] = formattedDate || (value ? String(value).trim() : '');
                                } else {
                                    patientData[headerMapping[patientKey]] = value ? String(value).trim() : '';
                                }
                            }
                          });

                          // Basic validation: ensure essential fields like name and phone are present
                          if (patientData.name && patientData.phone) {
                            patientsFromFile.push({
                              id: Date.now().toString() + i, // Unique ID
                              name: patientData.name || '',
                              phone: patientData.phone || '',
                              dob: patientData.dob || '',
                              mrn: patientData.mrn || '',
                              surgeryDate: patientData.surgeryDate || '',
                              surgeryType: patientData.surgeryType || '',
                              surgerySite: patientData.surgerySite || '',
                              scenario: 'post_surgery', // Hardcode scenario for patients from Excel
                              status: 'Pending'
                            });
                          } else {
                            console.warn("Skipping row due to missing name or phone:", row);
                          }
                        }

                        if (patientsFromFile.length > 0) {
                            setPatientQueue(prevQueue => [...prevQueue, ...patientsFromFile]);
                            setFeedbackMessage({type: 'success', message: `Successfully added ${patientsFromFile.length} patients from ${file.name}.`});
                        } else {
                            throw new Error("No valid patient data found in the Excel file matching expected headers.");
                        }

                      } catch (parseError) {
                        console.error("Error parsing Excel file:", parseError);
                        setFeedbackMessage({ type: 'error', message: `Error parsing Excel: ${parseError.message}` });
                      }
                    };

                    reader.onerror = (error) => {
                        console.error("FileReader error:", error);
                        setFeedbackMessage({ type: 'error', message: `Error reading file: ${error.message}` });
                        setIsLoading(false);
                    };

                    reader.readAsBinaryString(file); // Important for xlsx library

                  } catch (error) {
                    console.error("Error processing file:", error);
                    setFeedbackMessage({ type: 'error', message: `Error processing file: ${error.message}` });
                  }
                  // Reset file input to allow selecting the same file again
                  e.target.value = null;
                  setIsLoading(false);
                }
              }}
            />
            <button 
              type="button" 
              onClick={() => document.getElementById('excelUpload').click()}
              className="w-full sm:w-auto flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              disabled={isCallingQueue}
            >
              Upload Patient List (Excel)
            </button>
        </div>
        
        {feedbackMessage && (
          <div className={`p-3 mt-4 rounded-md text-sm ${
              feedbackMessage.type === 'success' ? 'bg-green-50 text-green-700' : 
              feedbackMessage.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
            }`}
          >
            {feedbackMessage.message}
          </div>
        )}
      </section>

      {/* Patient Queue Display */}
      {patientQueue.length > 0 && (
        <section className="mt-10">
          <h3 className="text-lg font-semibold text-gray-900">Calling Queue</h3>
          <div className="mt-4 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Name</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Phone</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Surgery Date</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Surgery Type</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6"><span className="sr-only">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {patientQueue.map((patient) => (
                        <tr key={patient.id}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{patient.name}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{patient.phone}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{patient.surgeryDate || 'N/A'}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{patient.surgeryType || 'N/A'}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              callStatuses[patient.id]?.success === true ? 'bg-green-100 text-green-800' :
                              callStatuses[patient.id]?.success === false ? 'bg-red-100 text-red-800' :
                              callStatuses[patient.id]?.status === 'Calling...' ? 'bg-yellow-100 text-yellow-800 animate-pulse' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {callStatuses[patient.id]?.status || patient.status}
                            </span>
                            {callStatuses[patient.id]?.message && !callStatuses[patient.id]?.success && (
                                <p className="text-xs text-red-600 mt-1 truncate" title={callStatuses[patient.id]?.message}>
                                    Error: {callStatuses[patient.id]?.message.substring(0,50)}...
                                </p>
                            )}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <button onClick={() => handleRemovePatientFromQueue(patient.id)} disabled={isCallingQueue || callStatuses[patient.id]?.status === 'Calling...'}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50">
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <button type="button" onClick={handleStartCallingQueue} disabled={isCallingQueue || patientQueue.length === 0}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                ${isCallingQueue ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50`}
            >
              {isCallingQueue ? 'Calling Queue...' : `Start Calling Queue (${patientQueue.length} Patients)`}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

export default NewCall; 